import React, { createContext, useContext, useState, useCallback } from 'react';

// Create context
const ApiCacheContext = createContext(null);

/**
 * Provider component for API caching
 * @param {Object} props - Component props
 * @returns {JSX.Element} Provider component
 */
export const ApiCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  /**
   * Get cached data if it exists and is still valid
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if not found/expired
   */
  const getCachedData = useCallback((key) => {
    const cachedItem = cache.get(key);
    if (!cachedItem) {
      return null;
    }

    const now = Date.now();
    if (now - cachedItem.timestamp > CACHE_DURATION) {
      // Cache expired, remove it
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(key);
        return newCache;
      });
      return null;
    }

    console.log(`Using cached data for key: ${key}`);
    return cachedItem.data;
  }, [cache, CACHE_DURATION]);

  /**
   * Set data in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  const setCachedData = useCallback((key, data) => {
    console.log(`Caching data for key: ${key}`);
    setCache(prev => new Map(prev).set(key, {
      data,
      timestamp: Date.now()
    }));
  }, []);

  /**
   * Clear specific cache entry
   * @param {string} key - Cache key to clear
   */
  const clearCacheEntry = useCallback((key) => {
    console.log(`Clearing cache for key: ${key}`);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(key);
      return newCache;
    });
  }, []);

  /**
   * Clear all cache entries
   */
  const clearAllCache = useCallback(() => {
    console.log('Clearing all cache');
    setCache(new Map());
  }, []);

  /**
   * Generate cache key from parameters
   * @param {string} endpoint - API endpoint
   * @param {Object} params - API parameters
   * @returns {string} Cache key
   */
  const generateCacheKey = useCallback((endpoint, params = {}) => {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${endpoint}:${JSON.stringify(sortedParams)}`;
  }, []);

  /**
   * Cached API call wrapper
   * @param {string} endpoint - API endpoint name
   * @param {Function} apiCall - API call function
   * @param {Object} params - API parameters
   * @returns {Promise<any>} API response
   */
  const cachedApiCall = useCallback(async (endpoint, apiCall, params = {}) => {
    const cacheKey = generateCacheKey(endpoint, params);
    
    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Make API call
    console.log(`Making API call for: ${endpoint}`);
    const data = await apiCall(params);
    
    // Cache the result
    setCachedData(cacheKey, data);
    
    return data;
  }, [generateCacheKey, getCachedData, setCachedData]);

  const value = {
    getCachedData,
    setCachedData,
    clearCacheEntry,
    clearAllCache,
    generateCacheKey,
    cachedApiCall
  };

  return (
    <ApiCacheContext.Provider value={value}>
      {children}
    </ApiCacheContext.Provider>
  );
};

/**
 * Custom hook to use the API cache context
 * @returns {Object} API cache context value
 */
export const useApiCache = () => {
  const context = useContext(ApiCacheContext);
  
  if (!context) {
    throw new Error('useApiCache must be used within an ApiCacheProvider');
  }
  
  return context;
};

export default ApiCacheContext;
